'use client';

import { Suspense, useState, useEffect } from 'react';
import { markNeedsSaveAfterLogin } from '@/utils/sessionSaveCache';
import { useRouter, useSearchParams } from 'next/navigation';
import { Loader, DollarSign, Award, TrendingUp, RefreshCw, Users, BarChart2 } from 'lucide-react';
import { getSession, useSession, signIn } from 'next-auth/react';
import styles from '@/styles/auth/login.module.css';
import ls from '@/styles/auth/leadscale.module.css';
import Link from 'next/link';
import { getRoleBasedRedirectPath } from '@/utils/roleRedirects';
import { resolveAuthTheme } from '@/utils/domainBranding';
import {
  getLeadScaleLeadsUrl,
  isLeadScalePortalUser,
} from '@/utils/advertiserAccess';
import { isLeadScaleHost } from '@/utils/domainAccess';
import {
  LeadScaleMark,
  LeadScaleBrandWordmark,
  SignalStage,
} from '@/components/leadscale/SignalStage';
import Swal from 'sweetalert2';

function redirectAfterLogin(user, router) {
  if (isLeadScalePortalUser(user) && typeof window !== 'undefined') {
    if (!isLeadScaleHost(window.location.hostname)) {
      window.location.replace(getLeadScaleLeadsUrl());
      return;
    }
  }
  router.replace(getRoleBasedRedirectPath(user?.role?.id ?? user?.id_role));
}

function LoginForm({ initialHostname = '' }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [hostname, setHostname] = useState(initialHostname);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showResendLink, setShowResendLink] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const { data: session, status } = useSession();

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname) {
      setHostname(window.location.hostname);
    }
  }, []);

  const { branding, isAfflandTheme, isLeadScaleTheme } = resolveAuthTheme(hostname, searchParams);
  const isAfflandParam = searchParams.get('affland') === '1';
  const isLeadScaleParam =
    searchParams.get('leadscale') === '1' || searchParams.get('advertiser') === '1';
  const themeQuery = isAfflandParam
    ? '?affland=1'
    : isLeadScaleParam
      ? '?leadscale=1'
      : '';
  const registerHref = `/register${themeQuery}`;
  const loginHref = `/login${themeQuery}`;

  useEffect(() => {
    const checkReferrer = () => {
      const referrer = document.referrer;
      const currentDomain = window.location.hostname;
      const externalDomains = ['aff.id', 'affland.id', 'leadscaleglobal.com'];
      let shouldShowResendLink = false;

      if (referrer) {
        try {
          const referrerDomain = new URL(referrer).hostname;
          const isFromExternalDomain = externalDomains.some((domain) =>
            referrerDomain.includes(domain)
          );
          const isFromDifferentDomain =
            referrerDomain !== currentDomain && !referrerDomain.includes('localhost');
          if (isFromExternalDomain || isFromDifferentDomain) {
            shouldShowResendLink = true;
            localStorage.setItem('external_referrer', referrerDomain);
          }
        } catch {
          /* ignore */
        }
      }

      const storedReferrer = localStorage.getItem('external_referrer');
      if (storedReferrer && !shouldShowResendLink) {
        const isFromStoredExternalDomain = externalDomains.some((domain) =>
          storedReferrer.includes(domain)
        );
        const isFromStoredDifferentDomain =
          storedReferrer !== currentDomain && !storedReferrer.includes('localhost');
        if (isFromStoredExternalDomain || isFromStoredDifferentDomain) {
          shouldShowResendLink = true;
        }
      }

      if (isAfflandParam || isLeadScaleTheme) {
        shouldShowResendLink = true;
      }

      if (searchParams.get('utm_source')) {
        shouldShowResendLink = true;
      }

      setShowResendLink(shouldShowResendLink);
    };

    checkReferrer();
  }, [isAfflandParam, isLeadScaleTheme, searchParams]);

  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      redirectAfterLogin(session.user, router);
      return;
    }
    if (status !== 'loading') {
      setLoading(false);
    }
  }, [status, session, router]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await signIn('credentials', {
        email: formData.email,
        password: formData.password,
        redirect: false,
      });

      if (result?.error) {
        await Swal.fire({ heightAuto: false, title: 'Invalid email or password' });
        setLoading(false);
        return;
      }

      markNeedsSaveAfterLogin();
      await new Promise((resolve) => setTimeout(resolve, 100));
      const nextSession = await getSession();

      if (!nextSession?.user) {
        return;
      }

      redirectAfterLogin(nextSession.user, router);
    } catch (err) {
      await Swal.fire({ heightAuto: false, title: err.message });
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const afflandNav = (
    <ul className={styles.afflandNav}>
      <li>
        <a href="https://affland.id/index.html" target="_blank" rel="noopener noreferrer">
          Home
        </a>
      </li>
      <li>
        <a href="https://affland.id/about.html" target="_blank" rel="noopener noreferrer">
          About us
        </a>
      </li>
      <li>
        <a href="https://affland.id/offer.html" target="_blank" rel="noopener noreferrer">
          Our Offers
        </a>
      </li>
      <li>
        <a href="https://affland.id/contact.html" target="_blank" rel="noopener noreferrer">
          Contact
        </a>
      </li>
      <li>
        <Link href={registerHref}>Registration</Link>
      </li>
      <li>
        <Link href={loginHref} className={styles.active}>
          Login
        </Link>
      </li>
    </ul>
  );

  const afflandMarketingContent = (
    <div className={styles.afflandContentCol}>
      <h2 className={styles.afflandContentTitle}>Work with us to get:</h2>
      <div className={styles.afflandFeatureGrid}>
        <div className={styles.afflandFeatureCard}>
          <DollarSign className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>High</strong> Payouts
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <Award className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Best</strong> author&apos;s offers
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <TrendingUp className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Approval rate</strong> 35% +
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <RefreshCw className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Regular</strong> Payments
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <Users className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Professional</strong> Support
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <BarChart2 className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>API &</strong> Live Report
          </div>
        </div>
      </div>
      <p className={styles.afflandSlogan}>Get the profit from the first lead</p>
      <p className={styles.afflandSloganSub}>Great payouts are waiting for you !</p>
      <Link href={registerHref} className={styles.afflandRegButton}>
        Registration →
      </Link>
    </div>
  );

  const logoBlock = isLeadScaleTheme ? (
    <div className="text-center mb-6">
      <LeadScaleMark />
    </div>
  ) : (
    <div className="text-center mb-8">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={branding.logo}
        alt="Logo"
        width="200"
        height="80"
        className={`mx-auto mb-6 ${isAfflandTheme ? styles.afflandLogo : ''}`}
      />
    </div>
  );

  const formContent = (
    <>
      {logoBlock}
      {isLeadScaleTheme ? (
        <>
          <h2 className={ls.cardTitle}>Sign in</h2>
          <p className={ls.cardSub}>Affiliate and advertiser accounts — one login</p>
        </>
      ) : null}

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className={`block text-sm font-medium mb-1 ${isLeadScaleTheme ? 'text-slate-200' : 'text-gray-700'}`}
            >
              E-mail:
            </label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="test@gmail.com"
              required
              className={`appearance-none relative block w-full px-3 py-2 border rounded-md focus:outline-none sm:text-sm ${
                isLeadScaleTheme
                  ? 'border-white/20 bg-white/10 text-white placeholder-white/40 focus:ring-[#c8ff35] focus:border-[#c8ff35]'
                  : `border-gray-300 placeholder-gray-500 text-gray-900 ${
                      isAfflandTheme
                        ? 'focus:ring-green-500 focus:border-green-500'
                        : 'focus:ring-blue-500 focus:border-blue-500'
                    }`
              }`}
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
          <div>
            <label
              htmlFor="password"
              className={`block text-sm font-medium mb-1 ${isLeadScaleTheme ? 'text-slate-200' : 'text-gray-700'}`}
            >
              Password:
            </label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder="**********"
              required
              className={`appearance-none relative block w-full px-3 py-2 border rounded-md focus:outline-none sm:text-sm ${
                isLeadScaleTheme
                  ? 'border-white/20 bg-white/10 text-white placeholder-white/40 focus:ring-[#c8ff35] focus:border-[#c8ff35]'
                  : `border-gray-300 placeholder-gray-500 text-gray-900 ${
                      isAfflandTheme
                        ? 'focus:ring-green-500 focus:border-green-500'
                        : 'focus:ring-blue-500 focus:border-blue-500'
                    }`
              }`}
              value={formData.password}
              onChange={handleChange}
              disabled={loading}
            />
          </div>
        </div>

        {error ? <div className="text-red-500 text-sm text-center">{error}</div> : null}

        <div>
          <button
            type="submit"
            disabled={loading}
            className={
              isLeadScaleTheme
                ? ls.buttonPrimary
                : isAfflandTheme
                  ? styles.afflandButton
                  : 'w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50'
            }
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin mr-2 inline" />
                Signing in...
              </>
            ) : (
              'Login'
            )}
          </button>
        </div>
      </form>

      {isLeadScaleTheme ? (
        <div className="mt-6 flex flex-col gap-2 text-center text-sm">
          <Link href={`/forgot-password${themeQuery}`} className={ls.link}>
            Forgot Password?
          </Link>
          <Link href={registerHref} className={ls.link}>
            Create an account
          </Link>
          {showResendLink ? (
            <Link href={`/register/resend-confirmation${themeQuery}`} className={ls.link}>
              Resend Confirmation
            </Link>
          ) : null}
        </div>
      ) : isAfflandTheme ? (
        <div className={styles.afflandLinks}>
          <Link href={`/forgot-password${themeQuery}`} className={styles.afflandLinkItem}>
            Forgot Password?
          </Link>
          {showResendLink ? (
            <>
              <span className={styles.afflandLinkSep}>·</span>
              <Link href={registerHref} className={styles.afflandLinkItem}>
                Sign Up
              </Link>
              <span className={styles.afflandLinkSep}>·</span>
              <Link
                href={`/register/resend-confirmation${themeQuery}`}
                className={styles.afflandLinkItem}
              >
                Resend Confirmation
              </Link>
            </>
          ) : null}
        </div>
      ) : (
        <>
          <div className={styles.linkContainer}>
            <div className={styles.linkItem}>
              <Link href={`/forgot-password${themeQuery}`}>Forgot Password?</Link>
            </div>
          </div>
          {showResendLink ? (
            <div className={styles.linkContainer}>
              <div className={styles.linkItem}>
                <Link href={registerHref}>Sign Up</Link>
              </div>
              <div className={styles.linkItem}>
                <Link href={`/register/resend-confirmation${themeQuery}`}>Resend Confirmation</Link>
              </div>
            </div>
          ) : null}
        </>
      )}
    </>
  );

  const spinner = (colorClass) => (
    <div className="flex flex-col items-center">
      <Loader className={`w-8 h-8 animate-spin ${colorClass}`} />
      {status === 'authenticated' ? (
        <span className="mt-4 text-gray-400">Redirecting...</span>
      ) : null}
    </div>
  );

  if (loading || status === 'authenticated') {
    if (isLeadScaleTheme) {
      return (
        <div className={ls.page}>
          <div className={ls.pageGrid} />
          <main className={ls.main}>
            <div className={ls.card}>{spinner('text-[#c8ff35]')}</div>
          </main>
        </div>
      );
    }
    if (isAfflandTheme) {
      return (
        <div className={styles.afflandLoginPage}>
          <header className={styles.afflandHeader}>
            <nav>{afflandNav}</nav>
          </header>
          <main className={styles.afflandMain}>
            <div className={styles.afflandMainInner}>
              <div className={styles.afflandFormCol}>
                <div className={styles.afflandCard}>{spinner('text-[#28a745]')}</div>
              </div>
              {afflandMarketingContent}
            </div>
          </main>
          <footer className={styles.afflandFooter}>
            <span>
              © 2026 Affland by EXODO. All rights reserved.{' '}
              <Link href="/privacy-policy">Privacy Policy</Link>
            </span>
          </footer>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center relative">
        <div className={`absolute inset-0 z-0 ${styles.bgExodo}`} />
        <div className="bg-white p-8 rounded-lg shadow-lg z-10 w-full max-w-md">
          {spinner('text-blue-500')}
        </div>
      </div>
    );
  }

  if (isLeadScaleTheme) {
    return (
      <div className={ls.page}>
        <div className={ls.pageGrid} />
        <header className={ls.header}>
          <LeadScaleBrandWordmark />
          <div className={ls.headerLinks}>
            <Link href="/">Home</Link>
            <Link href={registerHref}>Register</Link>
          </div>
        </header>
        <div className={ls.authSplit}>
          <div className={ls.card}>{formContent}</div>
          <div className={ls.authVisual} aria-hidden="true">
            <SignalStage />
          </div>
        </div>
        <footer className={ls.footer}>© LeadScale Global Partner</footer>
      </div>
    );
  }

  if (isAfflandTheme) {
    return (
      <div className={styles.afflandLoginPage}>
        <header className={styles.afflandHeader}>
          <nav>{afflandNav}</nav>
        </header>
        <main className={styles.afflandMain}>
          <div className={styles.afflandMainInner}>
            <div className={styles.afflandFormCol}>
              <div className={styles.afflandCard}>{formContent}</div>
            </div>
            {afflandMarketingContent}
          </div>
        </main>
        <footer className={styles.afflandFooter}>
          <span>
            © 2026 Affland by EXODO. All rights reserved.{' '}
            <Link href="/privacy-policy">Privacy Policy</Link>
          </span>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      <div className={`absolute inset-0 z-0 ${styles.bgExodo}`} />
      <div className="bg-white p-8 rounded-lg shadow-lg z-10 w-full max-w-md">{formContent}</div>
    </div>
  );
}

function LoginSuspenseFallback({ initialHostname }) {
  const { isLeadScaleTheme, isAfflandTheme } = resolveAuthTheme(initialHostname, {});
  if (isLeadScaleTheme) {
    return (
      <div className={ls.page}>
        <div className={ls.pageGrid} />
        <main className={ls.main}>
          <div className={ls.card}>
            <Loader className="w-8 h-8 animate-spin text-[#c8ff35] mx-auto" />
          </div>
        </main>
      </div>
    );
  }
  if (isAfflandTheme) {
    return (
      <div className={styles.afflandLoginPage}>
        <main className={styles.afflandMain}>
          <div className="flex justify-center items-center p-12">
            <Loader className="w-8 h-8 animate-spin text-[#28a745]" />
          </div>
        </main>
      </div>
    );
  }
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader className="w-8 h-8 animate-spin text-gray-500" />
    </div>
  );
}

export default function LoginClient({ initialHostname = '' }) {
  return (
    <Suspense fallback={<LoginSuspenseFallback initialHostname={initialHostname} />}>
      <LoginForm initialHostname={initialHostname} />
    </Suspense>
  );
}
